/**
 * Comparing building-attribute values across sources.
 *
 * The same fact arrives written differently: an IFC model states a whole area
 * as the float `9850.0`, a PDF says `9850`, an Estonian register `9850,00`.
 * Compared as strings those are three conflicting candidates. Compared here
 * they are one value with three sources.
 *
 * The numeric rule is deliberately narrow — a false merge hides a genuine
 * disagreement, which is worse than showing the same number twice:
 * - Only a plain decimal counts: an optional minus, an integer part, at most
 *   one `.` or `,` decimal separator. Units, exponents, `%` and digit grouping
 *   stay text.
 * - A leading zero ("01234") is a code, not a quantity, so it stays text.
 * - "1,500" / "1.500" is thousands in one convention and a decimal in the
 *   other, so it stays text and never merges with "1.5" or "1500".
 *
 * The chatbot's `canonical_value` (chatbot/building_data.py) applies the same
 * rule, so the sidebar and the chatbot report the same set of values.
 */

const PLAIN_DECIMAL_RE = /^(-?)(0|[1-9]\d*)(?:[.,](\d+))?$/;
const GROUPING_SHAPED_RE = /^-?[1-9]\d{0,2}[.,]\d{3}$/;

/**
 * The number written without trailing zeros ("9850.0" → "9850", "12,50" →
 * "12,5"), keeping the separator it was written with, or null when `text` is
 * not a plain decimal. Done on the digits rather than through `Number` so no
 * precision is lost.
 */
function trimmedNumber(text: string): string | null {
  if (GROUPING_SHAPED_RE.test(text)) return null;
  const match = PLAIN_DECIMAL_RE.exec(text);
  if (!match) return null;
  const [, sign, whole, fraction = ""] = match;
  const separator = text.includes(",") ? "," : ".";
  const trimmedFraction = fraction.replace(/0+$/, "");
  const number = trimmedFraction ? `${whole}${separator}${trimmedFraction}` : whole;
  return number === "0" ? number : `${sign}${number}`;
}

/**
 * The key two sources must share to report the same value: numbers by value,
 * everything else case- and whitespace-insensitively ("No" = "no",
 * "  120  " = "120", "TP1" = "tp1", but "TP1" ≠ "TP 1").
 */
export function canonicalAttributeValue(value: string): string {
  const trimmed = value.trim();
  const number = trimmedNumber(trimmed);
  if (number !== null) return number.replace(",", ".");
  return trimmed.toLowerCase().replace(/\s+/g, " ");
}

export function sameAttributeValue(a: string, b: string): boolean {
  return canonicalAttributeValue(a) === canonicalAttributeValue(b);
}

/**
 * `value` as it should be shown and saved: a plain decimal loses its trailing
 * zeros but keeps its own decimal separator; anything else is unchanged.
 */
export function displayAttributeValue(value: string): string {
  return trimmedNumber(value.trim()) ?? value;
}
